/* eslint-disable eqeqeq */
import PropTypes from 'prop-types'
import { Button, Checkbox, Grid, GridItem, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { createProjectContact, getProjectContacts, getCustomers, deleteProjectContact, updateProjectContact } from 'api'
import { useEffect, useState } from 'react'
import { useAppContext } from 'hooks/useAppContext'
import { UiSelect } from 'components/common'

export const ProjectContacts = ({ projectId, project }) => {
  const { createToast } = useAppContext()
  const [contacts, setContacts] = useState([])
  const [customer, setCustomer] = useState(null)
  const [isAdding, setIsAdding] = useState(false)

  const load = async () => {
    if (!projectId) {
      return
    }
    try {
      const { rows } = await getProjectContacts(projectId)
      setContacts(rows)
    } catch (err) {
      createToast('error', 'Error', 'Failed to load project contacts.')
    }
  }

  useEffect(() => {
    load()
  }, [projectId])

  const handleAdd = async () => {
    if (!customer || !customer.id) {
      createToast('warning', 'Warning', 'Please select a customer to add.')
      return
    }

    setIsAdding(true)
    try {
      await createProjectContact({ projectId, customerId: customer.id })
      setCustomer(null)
      load()
      setIsAdding(false)
      createToast('success', 'Success', 'Project contact added.')
    } catch (err) {
      if (err.response.status === 400) {
        createToast('info', 'Info', err.response.data.error)
      } else {
        createToast('error', 'Error', 'Failed to add the contact.')
      }
      setIsAdding(false)
    }
  }

  return (
    <>
      {contacts.length > 0 ? (
        <Grid templateColumns="repeat(7, 1fr)" gap={4} mb={8} alignItems="center">
          <GridItem><Text fontSize="sm" fontWeight="bold">Company</Text></GridItem>
          <GridItem><Text fontSize="sm" fontWeight="bold">Name</Text></GridItem>
          <GridItem><Text fontSize="sm" fontWeight="bold">Address</Text></GridItem>
          <GridItem><Text fontSize="sm" fontWeight="bold">Phone</Text></GridItem>
          <GridItem><Text fontSize="sm" fontWeight="bold">Email</Text></GridItem>
          <GridItem><Text fontSize="sm" fontWeight="bold">Receive Emails</Text></GridItem>
          <GridItem></GridItem>
          {contacts.map(contact => <ProjectContactRow key={contact.id} contact={contact} reload={load} />)}
        </Grid>
      ) : (
        <Text fontSize="sm" mb={8}>No contacts found.</Text>
      )}
      <Grid templateColumns="repeat(4, 1fr)" columnGap={4} rowGap={2}>
        <GridItem>
          <UiSelect
            value={customer}
            onChange={v => {
              setCustomer(v)
            }}
            loadData={(page, search) => getCustomers(page, 20, search, 'name', 'asc')}
            apiRespProp="rows"
            getOptionValue={v => v.id}
            getOptionLabel={v => v.name}
            placeholder="Search Customers"
            noOptionsMessage={() => 'No Customers Found'}
            required={true}
            minW="500px"
          />
        </GridItem>
        <GridItem>
          <Button size="sm" colorScheme="blue" onClick={handleAdd} isLoading={isAdding}>Add Contact To Project</Button>
        </GridItem>
      </Grid>
    </>
  )
}

ProjectContacts.propTypes = {
  projectId: PropTypes.number,
  project: PropTypes.object
}

const ProjectContactRow = ({ contact, reload }) => {
  const { createToast } = useAppContext()
  const disclosure = useDisclosure()
  const [isRemoving, setIsRemoving] = useState(false)
  const [isUpdatingEmails, setIsUpdatingEmails] = useState(false)
  const [receiveAutomaticEmails, setReceiveAutomaticEmails] = useState(!!contact.receiveAutomaticEmails)

  useEffect(() => {
    setReceiveAutomaticEmails(!!contact.receiveAutomaticEmails)
  }, [contact.receiveAutomaticEmails])

  const handleRemove = async () => {
    setIsRemoving(true)
    try {
      await deleteProjectContact(contact.id)
      setIsRemoving(false)
      reload()
      createToast('success', 'Success', 'Deleted the contact.')
    } catch (err) {
      createToast('error', 'Error', 'Failed to remove the contact.')
      setIsRemoving(false)
    }
  }

  const handleReceiveEmailsChange = async (checked) => {
    const previous = receiveAutomaticEmails
    setReceiveAutomaticEmails(checked)
    setIsUpdatingEmails(true)
    try {
      await updateProjectContact(contact.id, { receiveAutomaticEmails: checked })
      setIsUpdatingEmails(false)
      createToast(
        'success',
        'Success',
        checked
          ? `${contact.name} will receive automatic emails.`
          : `${contact.name} will no longer receive automatic emails.`
      )
    } catch (err) {
      setReceiveAutomaticEmails(previous)
      setIsUpdatingEmails(false)
      createToast('error', 'Error', 'Failed to update email preference.')
    }
  }

  return (
    <>
      <GridItem><Text fontSize="xs">{contact.company}</Text></GridItem>
      <GridItem><Text fontSize="xs">{contact.name}</Text></GridItem>
      <GridItem><Text fontSize="xs">{`${contact.address}, ${contact.city}, ${contact.zip}, ${contact.state}`}</Text></GridItem>
      <GridItem><Text fontSize="xs">{contact.phone}</Text></GridItem>
      <GridItem><Text fontSize="xs">{contact.email}</Text></GridItem>
      <GridItem>
        <Checkbox
          isChecked={receiveAutomaticEmails}
          isDisabled={isUpdatingEmails}
          onChange={(e) => handleReceiveEmailsChange(e.target.checked)}
        >
          <Text fontSize="xs">Automatic emails</Text>
        </Checkbox>
      </GridItem>
      <GridItem><Button colorScheme="blue" size="xs" onClick={() => disclosure.onOpen()}>Remove</Button></GridItem>
      {disclosure.isOpen && (
        <ModalRemoveContact
          {...disclosure}
          contact={contact}
          isRemoving={isRemoving}
          handleRemove={handleRemove}
        />
      )}
    </>
  )
}

ProjectContactRow.propTypes = {
  contact: PropTypes.object,
  reload: PropTypes.func
}

const ModalRemoveContact = ({ contact, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Contact</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the contact - {contact.company}?</Text>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveContact.propTypes = {
  contact: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
